<template>
  <section class="exchange-form public-form">
    <div class="exchange-form__header">
      <h1 id="form-heading" class="exchange-header__title" data-testid="form-heading" :style="headingStyles">{{ state.headLabel }}</h1>
      <ZeroCommissionPromoDetails
        v-if="detailState.relationLink === relationLink && detailState.zeroCommission && props.isZeroCommissionAvailable && !detailState.isUsdtPurchaseRestricted"
        :title="detailState.zeroCommission"
      />
    </div>
    <div class="exchange-form__info">
      <div class="exchange-form__features">
        <ul class="exchange-features">
          <li v-for="item in features" class="list-item">
            <img
              class="list-item__icon"
              :src="getStaticAssetUrl('/resources/list_check_icon.svg')"
              alt="check"
            />
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
      <div class="exchange-form__app-badges">
        <div class="app-badge">
          <span class="app-badge__rating">{{ i18nInstanse.t('exchange-form.app-store-rating') }}</span>
          <img
              :src="getStaticAssetUrl('/resources/badges/apple.svg')"
              alt="Paybis: Bitcoin Wallet, Crypto on App Store"
              class="app-badge__logo"
              aria-hidden="true"
              width="24"
              height="24"
          />
          <span class="app-badge__store">App Store</span>
        </div>
        <div class="app-badge">
          <span class="app-badge__rating">{{ i18nInstanse.t('exchange-form.google-play-rating') }}</span>
          <img
              :src="getStaticAssetUrl('/resources/badges/google.svg')"
              alt="Paybis Crypto Wallet &amp; Bitcoin on Google Play"
              class="app-badge__logo"
              aria-hidden="true"
              width="24"
              height="24"
          />
          <span class="app-badge__store">Google Play</span>
        </div>
      </div>
    </div>
    <div
      class="exchange-form__body form-container"
      :class="{ 'form-container--error': hasFormError }"
    >
      <ExchangeFormLayout :showError="hasFormError">
        <template v-slot:from-payment-icons>
          <PaymentSystemIcons v-if="fromFiatFlow" />
        </template>
        <template v-slot:from>
          <PbPopup v-model="from.state.showDropDown">
            <template #action="{toggle, visible}">
              <ExchangeFormItem>
                <div class="exchange-input">
                  <label
                    class="exchange-input__label"
                    for="exchange-form-from"
                  >
                    {{ i18nInstanse.t('exchange-form.spend-label') }}
                  </label>
                  <ExchangeLoading v-if="fromLoading" />
                  <ExchangeInput
                    v-else
                    id="exchange-form-from"
                    data-testid="exchange-form-from"
                    v-model="from.state.amount"
                     :digits="fromFiatFlow ? 2 : 8"
                     @input="fromAmountHandler"
                   />
                </div>
                <ExchangeMoneyServiceLoading v-if="paymentMethodsState.loading" />
                <ExchangeMoneyService
                  v-else
                  :currency="fromSelectedMethod.currency"
                  :methodName="fromSelectedMethod.name"
                  :toggled="visible"
                  @click="toggle"
                />
              </ExchangeFormItem>
            </template>
            <template #default="{hide}">
              <ExchangeDropDownFiat
                v-if="fromFiatFlow"
                v-model="fromSelected"
                :crypto-currency="toSelected.currency"
                :flow="flow"
                @close="hide"
              />
              <template v-else-if="fromCryptoFlow">
                <ExchangeDropDownCryptoWallets
                  v-model="fromSelected"
                  :flow="flow"
                  :items="fromCryptoCurrenciesList"
                  :loading="fromCryptoCurrenciesState.loading"
                  :relationLink="relationLink"
                  @close="hide"
                />
              </template>
            </template>
          </PbPopup>
        </template>
        <template v-slot:from-error>
          <ExchangeFormItemError v-if="isCurrentRelationRestricted || from.state.error">
            <span v-if="isCurrentRelationRestricted">{{ i18nInstanse.t('exchange-form.usdt-purchase-restriction') }}</span>
            <div v-else-if="minMaxAmountError">
              {{minMaxAmountError.prefix}}
              <span class="exchange-form__error-action" @click="handleMinMaxAmountClick">{{minMaxAmountError.amount}}</span>
              {{minMaxAmountError.suffix}}
            </div>
            <span v-else>{{ from.state.error }}</span>
          </ExchangeFormItemError>
        </template>
        <template v-slot:from-description>
          <ExchangeFormPromoComponent
            v-if="props.isPromoCodeAvailable"
            class="exchange-form__promocode-block"
            v-model="promoCodeState.data.code"
            :capture="promoCodeState.data.capture"
            :error="promoCodeState.data.error"
          />
        </template>
        <template v-slot:to>
          <PbPopup v-model="to.state.showDropDown">
            <template #action="{toggle, visible}">
              <ExchangeFormItem>
                <div class="exchange-input">
                  <label
                    class="exchange-input__label"
                    for="exchange-form-to"
                  >
                    {{ i18nInstanse.t('exchange-form.receive-label') }}
                  </label>
                  <ExchangeLoading v-if="toLoading" />
                  <ExchangeInput
                    v-else
                    id="exchange-form-to"
                    data-testid="exchange-form-to"
                    v-model="to.state.amount"
                    :digits="toCryptoFlow ? 8 : 2"
                    @input="toAmountHandler"
                  />
                </div>
                <ExchangeMoneyServiceLoading v-if="payoutMethodsState.loading" />
                <ExchangeMoneyService
                  v-else
                  :currency="toSelectedMethod.currency"
                  :methodName="toSelectedMethod.name"
                  :toggled="visible"
                  @click="toggle"
                />
              </ExchangeFormItem>
            </template>
            <template #default="{hide}">
              <ExchangeDropDownFiat
                v-if="toFiatFlow"
                v-model="toSelected"
                :flow="flow"
                :crypto-currency="fromSelected.currency"
                @close="hide"
              />
              <template v-else-if="toCryptoFlow">
                <ExchangeDropDownCrypto
                  v-model="toSelected"
                  :flow="flow"
                  :items="toCryptoCurrenciesList"
                  :loading="toCryptoCurrenciesState.loading"
                  @close="hide"
                />
              </template>
            </template>
          </PbPopup>
        </template>
        <template v-slot:to-description></template>
        <template v-slot:action>
          <ExchangeAction
            :loading="exchangeActionLoading"
            :disabled="isCurrentRelationRestricted || isRelationDetailsPending"
            @click.native="createRegularTransaction"
          >
            {{ state.actionLabel }}
          </ExchangeAction>
          <TransitionHeight>
            <div class="form-layout__transition-block" v-if="canShowApplePayButton"> 
              <div class="form-layout__transition-wrapper">
                <small>
                  {{ i18nInstanse.t('exchange-form.pay-instantly-with') }}
                </small>
                <ApplePayButton
                  class="apple-pay-button button button--secondary button--large"
                  :client-context-id-header="clientContextIdHeader"
                  one-click
                  :amount="from.state.amount"
                  :currency="from.state.selected.currency"
                  :has-error="hasFormError || isRelationDetailsPending"
                  :disabled="isCurrentRelationRestricted || isRelationDetailsPending"
                  :loading="exchangeActionLoading"
                  @payment-authorized="createApplePayTransaction"
                  @error="createApplePayTransaction"
                />
              </div>
            </div>
          </TransitionHeight>
        </template>
      </ExchangeFormLayout>
      <FeesDetails :endDate="quote?.expiresAt" :rate="rate" :fees="commissionsToShow"/>
    </div>
  </section>
</template>
<script lang="ts" setup>
import store from '@/store/assets';
import { getLocaleByLanguage } from '@paybis/frontend-common-lib/src/services/util.js';
import MoneyServiceTypes from '@paybis/frontend-common-lib/src/models/money-service/type.js';
import { getClientContextIdHeader, regenerateClientContextIdHeader } from '@paybis/frontend-common-lib/src/services/headers/clientContextId.js';
import { useMemoize } from '@vueuse/core';
import axios from 'axios';
import { debounce } from 'lodash-es';
import { computed, onBeforeMount, reactive, ref, watch, watchEffect } from 'vue';
import { useRouter } from 'vue-router/composables';
import Decimal from 'decimal.js';
import FeesDetails from '@kano/components/ExchangeFormV2/FeesDetails/FeesDetail.vue';
import ZeroCommissionPromoDetails from '@kano/components/ConvertDetails/ZeroCommissionPromoDetails.vue';
import ExchangeAction from '@/components/ExchangeFormV2/ExchangeAction.vue';
import ExchangeDropDownCrypto from '@kano/components/ExchangeFormV2/ExchangeDropDownCrypto.vue';
import ExchangeDropDownCryptoWallets from '@kano/components/ExchangeFormV2/ExchangeDropDownCryptoWallets.vue';
import ExchangeDropDownFiat from '@kano/components/ExchangeFormV2/ExchangeDropDownFiat.vue';
import ExchangeFormItem from '@/components/ExchangeFormV2/ExchangeFormItem.vue';
import ExchangeFormItemError from '@kano/components/ExchangeFormV2/ExchangeFormItemError.vue';
import ExchangeFormLayout from '@kano/components/ExchangeFormV2/ExchangeFormLayout.vue';
import ExchangeFormPromoComponent from '@kano/components/ExchangeFormV2/ExchangeFormPromoComponent.vue';
import ExchangeInput from '@/components/ExchangeFormV2/ExchangeInput.vue';
import ExchangeLoading from '@/components/ExchangeFormV2/ExchangeLoading.vue';
import ExchangeMoneyService from '@kano/components/ExchangeFormV2/ExchangeMoneyService.vue';
import ExchangeMoneyServiceLoading from '@/components/ExchangeFormV2/ExchangeMoneyServiceLoading.vue';
import PaymentSystemIcons from '@kano/components/ExchangeFormV2/PaymentSystemIcons.vue';
import PbPopup from '@kano/components/ExchangeFormV2/PbPopup.vue';
import {
  useCommission,
  useExchangeItem,
  usePromoCode,
  useSimpleAction,
  useSimpleList,
} from '@kano/composables';
import { formatNumberForCrypto } from '@/helpers/numberFormatter';
import { parseMinMaxAmountError } from '@paybis/frontend-common-lib/src/helpers/parseMinMaxAmountError';
import { i18n as i18nInstanse } from '@kano/i18n';
import {
  BaseOptionModel,
  CryptoOptionModel,
  FiatOptionModel,
  PayMethodQuoteModel,
  SwapCryptoQuoteModel,
} from '@kano/models';
import ExchangeFormService, { updateFormData } from '@kano/services/ExchangeFormService';
import PayMethodService from '@kano/services/PayMethodService';
import QuoteService, { QuoteValidationError } from '@kano/services/QuoteService';
import TransactionService from '@/services/TransactionService';
import {
  Currency,
  RelationLink,
  RequestCreateTransaction,
  RequestQuote,
  RequestSellCryptoQuote,
  RequestSwapCryptoQuote,
  ExchangeRate,
  Direction,
  Promo,
} from '@kano/types';
import { getStaticAssetUrl } from '@/helpers/cdnHelper';
import { ApplePayButton, checkIsApplePayAvailable } from '@paybis/apple-pay';
import { pushToDataLayer, ExchangeFormSource, DataLayerEvent } from '@paybis/frontend-common-lib/src/services/gtm';
import TransitionHeight from '@kano/components/TransitionHeight.vue';
import { Flow, Changed, flowMap, inputEventMap } from '@paybis/frontend-common-lib/src/types/exchangeForm/exchange-form';

interface Unit {
  id: string;
  currency: Currency;
  displayCurrency: string;
  icon: string;
  name: string;
  asset: Currency | null;
}

interface FlowCurrencyPair {
  defaultFiatCurrency: Currency;
  relationLink: RelationLink;
  from: Unit;
  to: Unit;
}

const clientContextIdHeader = ref(getClientContextIdHeader());

const props = withDefaults(defineProps<{
  from: Unit,
  to: Unit,
  fromAmount: string,
  toAmount: string,
  relationLink: RelationLink,
  feeCurrency: Currency,
  title: string,
  actionLabel: string,
  flow: Flow,
  defaultFlowCurrencyPairs: Record<Flow, FlowCurrencyPair>,
  availableBuyAssets: Currency[] | null,
  availableSellAssets: Currency[] | null,
  promo: PromoProp,
  lang: string,
  headingStyles: string,
  promoCode?: string | null,
  isPromoCodeAvailable?: boolean,
  isZeroCommissionAvailable?: boolean,
  isUsdtPurchaseRestricted: boolean,
  isUkUser: boolean,
  isCorporateAccount: boolean,
}>(), {
  isPromoCodeAvailable: true,
  isZeroCommissionAvailable: true,
  promoCode: null,
});
type PromoProp = Record<Promo, { isActive: boolean, title: string }>

const features = computed(() => [
  i18nInstanse.t('kano.hero.features.0'),
  i18nInstanse.t('kano.hero.features.1'),
  i18nInstanse.t('kano.hero.features.2'),
  i18nInstanse.t('kano.hero.features.3'),
  i18nInstanse.t('kano.hero.features.4'),
  i18nInstanse.t('kano.hero.features.5'),
]);

const exchangeActionLoading = computed(() => createTransactionLoading.value || createQuoteLoading.value || payoutMethodsState.loading || fetchRelationDetailsLoading.value);

const isApplePayAvailable = ref(false);

const canShowApplePayButton = computed(() => {
  if(props.isUkUser || props.isCorporateAccount) {
    return false;
  }

  return from.state?.selected?.item?.id === MoneyServiceTypes.TYPE_BANK_CARD
    && isApplePayAvailable.value;
});

onBeforeMount(async () => {
  isApplePayAvailable.value = await checkIsApplePayAvailable();
});

// Each flow has its own page in this theme, so the flow never changes in place.
const flow = ref<Flow>(props.flow);

const getSwapRelations = useMemoize(() => ExchangeFormService.swapRelations());
const { state: availableSwapRelations, fetch: fetchSwapRelations } = useSimpleList({
  loading: false,
  list: () => getSwapRelations(),
});

const from = useExchangeItem(new BaseOptionModel(props.from), flow.value, props.fromAmount);
const to = useExchangeItem(new BaseOptionModel(props.to), flow.value, props.toAmount);

const fromFiatFlow = computed(() => [Flow.FIAT_TO_CRYPTO, Flow.FIAT_TO_FIAT].includes(flow.value));
const fromCryptoFlow = computed(() => [Flow.CRYPTO_TO_FIAT, Flow.CRYPTO_TO_CRYPTO].includes(flow.value));
const toFiatFlow = computed(() => [Flow.CRYPTO_TO_FIAT, Flow.FIAT_TO_FIAT].includes(flow.value));
const toCryptoFlow = computed(() => [Flow.FIAT_TO_CRYPTO, Flow.CRYPTO_TO_CRYPTO].includes(flow.value));

const relationLink = ref(props.relationLink);
const detailState = reactive({
  relationLink: props.relationLink,
  isUsdtPurchaseRestricted: props.isUsdtPurchaseRestricted,
  zeroCommission: props.promo[Promo.ZERO_COMMISSION]?.isActive ? props.promo[Promo.ZERO_COMMISSION].title : null,
});

const isRelationDetailsPending = computed(() => detailState.relationLink !== relationLink.value);
const isCurrentRelationRestricted = computed(() => !isRelationDetailsPending.value && detailState.isUsdtPurchaseRestricted);
const hasFormError = computed(() => isCurrentRelationRestricted.value || Boolean(from.state.error) || Boolean(to.state.error));

const { state: promoCodeState } = usePromoCode();
promoCodeState.data.code = props.promoCode || '';

const state = reactive({
  headLabel: props.title,
  actionLabel: props.actionLabel,
});

function setDefaultFlowSelections(defaultFlow: FlowCurrencyPair) {
  from.state.selected = defaultFlow.from.asset !== null ? new CryptoOptionModel({
    ...defaultFlow.from,
    asset: defaultFlow.from.asset,
    defaultRelationLink: defaultFlow.relationLink,
    icon: defaultFlow.from.icon,
    defaultPayMethodCurrency: defaultFlow.defaultFiatCurrency,
  }) : new BaseOptionModel(defaultFlow.from);
  to.state.selected = defaultFlow.to.asset !== null ? new CryptoOptionModel({
    ...defaultFlow.to,
    asset: defaultFlow.to.asset,
    defaultRelationLink: defaultFlow.relationLink,
    icon: defaultFlow.to.icon,
    defaultPayMethodCurrency: defaultFlow.defaultFiatCurrency,
  }) : new BaseOptionModel(defaultFlow.to);
  relationLink.value = defaultFlow.relationLink;
  createQuote();
}

function setSelectedPaymentMethod() {
  const method = paymentMethodsState.items.find(item => item.id === from.state.selected.item.id) ?? paymentMethodsState.items[0];
  from.state.selected =  new BaseOptionModel({
    id: method.id,
    currency: method.currency,
    displayCurrency: method.currency,
    name: method.name,
  });
  relationLink.value = method.relationLink;
  createQuote();
}

function setSelectedPayoutMethod() {
  const method = payoutMethodsState.items.find(item => item.id === to.state.selected.item.id) ?? payoutMethodsState.items[0];
  to.state.selected =  new BaseOptionModel({
    id: method.id,
    currency: method.currency,
    displayCurrency: method.currency,
    name: method.name,
  });
  relationLink.value = method.relationLink;
  createQuote();
}

const router = useRouter();

const relationDetails = useMemoize((relationLink: RelationLink) => ExchangeFormService.relationDetails(relationLink));
const {
  loading: fetchRelationDetailsLoading,
  action: fetchRelationDetails,
} = useSimpleAction(async (requestedRelationLink: RelationLink) => {
  const { title, buttonLabel, languages, promo, isUsdtPurchaseRestricted } = await relationDetails(requestedRelationLink);
  if (requestedRelationLink !== relationLink.value) {
    return;
  }

  state.headLabel = title;
  state.actionLabel = buttonLabel;
  detailState.relationLink = requestedRelationLink;
  detailState.isUsdtPurchaseRestricted = isUsdtPurchaseRestricted;
  detailState.zeroCommission = promo[Promo.ZERO_COMMISSION]?.isActive ? promo[Promo.ZERO_COMMISSION].title : null;

  // XXX: Skip, if fiat payment method doesn't support selected language
  if (!ExchangeFormService.checkIfExistLanguage({
    languages,
    relationLink: requestedRelationLink,
    currentLanguage: props.lang,
  })) return;

  router.replace({ name: 'Home', params: { buyLink: requestedRelationLink } }).catch(() => {
  });
});

const { loading: createTransactionLoading, action: createTransaction } = useSimpleAction(
  () => {
    clientContextIdHeader.value = regenerateClientContextIdHeader();
    if (!requestedAmount.value) {
      from.state.error = i18nInstanse.t('exchange-form.error.no-amount').toString();
    }

    if (hasFormError.value || isRelationDetailsPending.value) {
      pushToDataLayer({
        event: DataLayerEvent.SUBMIT_ATTEMPT,
        isValidated: false,
      });

      return Promise.reject();
    }

    const payload = {
      quoteId: quoteId.value,
    } as RequestCreateTransaction;

    pushToDataLayer({
      event: DataLayerEvent.SUBMIT_ATTEMPT,
      isValidated: true,
    });

    return TransactionService.createTransaction(payload);
  },
  (error: unknown) => {
    if (axios.isAxiosError(error) || error?.status === 400) {
      from.state.error = error.data?.errors?.[0]?.message ?? null;
    }

    clientContextIdHeader.value = regenerateClientContextIdHeader();
  },
);

const gtmTransactionEventEmit = (attrs: Record<string, unknown> = { isApplePayOneClick: false }) => {
  window.dataLayer.push({
    ...attrs,
    event: 'begin_checkout',
    amountBuy: to.state.amount,
    amountSell: from.state.amount,
    currencyBuy: to.state.selected.currency,
    currencySell: from.state.selected.currency,
    ecommerce: {
      items: [{
        item_name: to.state.selected.currency,
        item_id: flow.value === 'crypto-to-fiat' ? from.state.selected.currency : to.state.selected.currency,
        quantity: 1,
      }],
    },
  });
};

const createApplePayTransaction = () => {
  gtmTransactionEventEmit({ isApplePayOneClick: true });
  return createTransaction();
};

const createRegularTransaction = () => {
  gtmTransactionEventEmit();
  return createTransaction();
};

// From CRYPTO
const fromCryptoCurrencies = useMemoize((flow: Flow) => ExchangeFormService.fromCryptoCurrencies(flow));
const { state: fromCryptoCurrenciesState, fetch: fetchFromCryptoCurrencies } = useSimpleList({
  list: () => fromCryptoCurrencies(flow.value),
});

const fromCryptoCurrenciesList = computed(() => {
  const list = fromCryptoCurrenciesState.items;
  const { availableSellAssets } = props;
  if (availableSellAssets) {
    return list.filter(item => availableSellAssets.includes(item.currency));
  }
  return list;
});

// To CRYPTO
const toCryptoCurrencies = useMemoize((flow: Flow) => ExchangeFormService.toCryptoCurrencies(flow));
const { state: toCryptoCurrenciesState, fetch: fetchToCryptoCurrencies } = useSimpleList({
  list: () => toCryptoCurrencies(flow.value),
});
const toCryptoCurrenciesList = computed(() => {
  const list = toCryptoCurrenciesState.items;
  const { availableBuyAssets } = props;
  if (availableBuyAssets) {
    return list.filter(item => availableBuyAssets.includes(item.currency));
  }
  return list;
});


const lastChanged = ref<Changed>((!props.fromAmount && props.toAmount > 0) ? Changed.TO : Changed.FROM);

const requestedAmount = computed(() => {
  switch (lastChanged.value) {
  case Changed.FROM:
  default:
    return from.state.amount;
  case Changed.FROM_EQUIVALENT:
    return from.state.equivalentAmount;
  case Changed.TO:
    return to.state.amount;
  case Changed.TO_EQUIVALENT:
    return to.state.equivalentAmount;
  }
});
const currencyCode = computed(() => {
  switch (lastChanged.value) {
  case Changed.FROM:
  case Changed.TO_EQUIVALENT:
    return from.state.selected.currency;
  }

  return to.state.selected.currency;
});
const requestedAmountType = computed(() => {
  switch (lastChanged.value) {
  case Changed.FROM:
  case Changed.FROM_EQUIVALENT:
    return Direction.FROM;
  }

  return Direction.TO;
});

const listFiatPaymentMethods = useMemoize(
  (from: Currency, to: Currency) => PayMethodService.listFiatPaymentMethods(from, to),
);

watch(() => from.state.showDropDown, async () => {
  if (!from.state.showDropDown) {
    return;
  }
  switch (flow.value) {
  case Flow.CRYPTO_TO_FIAT:
  case Flow.CRYPTO_TO_CRYPTO:
    fetchFromCryptoCurrencies();
    break;
  }
});

const { state: paymentMethodsState, fetch: paymentMethodsFetch } = useSimpleList({
  loading: false,
  list: () => listFiatPaymentMethods(from.state.selected.currency, to.state.selected.currency),
});
watch([() => from.state.selected.currency, () => to.state.selected.currency], () => {
  if (flow.value === Flow.FIAT_TO_CRYPTO) {
    paymentMethodsFetch();
  }
});

watch(() => paymentMethodsState.items, () => {
  if (flow.value === Flow.FIAT_TO_CRYPTO) {
    if (paymentMethodsState.items.length === 0) {
      setDefaultFlowSelections(props.defaultFlowCurrencyPairs[Flow.FIAT_TO_CRYPTO]);
    } else {
      setSelectedPaymentMethod();
    }
  }
});

const listFiatPayoutMethods = useMemoize(
  (from: Currency, to: Currency) => PayMethodService.listFiatPayoutMethods(from, to),
);

watch(() => to.state.showDropDown, () => {
  if (!to.state.showDropDown) {
    return;
  }
  switch (flow.value) {
  case Flow.FIAT_TO_CRYPTO:
  case Flow.CRYPTO_TO_CRYPTO:
    fetchToCryptoCurrencies();
    break;
  }
});

const { state: payoutMethodsState, fetch: payoutMethodsFetch } = useSimpleList({
  loading: false,
  list: () => listFiatPayoutMethods(from.state.selected.currency, to.state.selected.currency),
});
watch([() => from.state.selected.currency, () => to.state.selected.currency], () => {
  if (flow.value === Flow.CRYPTO_TO_FIAT) {
    payoutMethodsFetch();
  }
});

watch(flow, () => {
  switch (flow.value) {
  case Flow.FIAT_TO_CRYPTO:
    payoutMethodsState.items = [];
    break;
  case Flow.CRYPTO_TO_FIAT:
    paymentMethodsState.items = [];
    break;
  case Flow.CRYPTO_TO_CRYPTO:
    paymentMethodsState.items = [];
    payoutMethodsState.items = [];
    break;
  }
});
watch(() => payoutMethodsState.items, () => {
  if (flow.value === Flow.CRYPTO_TO_FIAT) {
    if (payoutMethodsState.items.length === 0) {
      setDefaultFlowSelections(props.defaultFlowCurrencyPairs[Flow.CRYPTO_TO_FIAT]);
    } else {
      setSelectedPayoutMethod();
    }
  }
});

const fromSelected = computed({
  get: () => from.state.selected,
  set: async (option: BaseOptionModel) => {
    from.state.selected = option;
    if (option instanceof FiatOptionModel) {
      relationLink.value = option.relationLink;
    }

    if (flow.value === Flow.FIAT_TO_CRYPTO && from.state.amount) {
      createQuote();
      return;
    }

    if (flow.value !== Flow.CRYPTO_TO_CRYPTO) {
      return;
    }
    // For swap crypto we need to make sure that we have an available relation for given selection
    await fetchSwapRelations();

    const fromSelectedAssetId = from.state.selected.assetId;
    const toSelectedAssetId = to.state.selected.assetId;

    const selectedRelation = availableSwapRelations.items.find(({ from: fromService, to: toService }) => fromService.assetId === fromSelectedAssetId && toService.assetId === toSelectedAssetId);
    if (selectedRelation) {
      relationLink.value = selectedRelation.link;
      createQuote();
      return;
    }
    const fromRelation = availableSwapRelations.items.find(({ from: fromService }) => fromService.assetId === fromSelectedAssetId);
    if (!fromRelation) {
      setDefaultFlowSelections(props.defaultFlowCurrencyPairs[flow.value]);
      return;
    }
    await fetchToCryptoCurrencies();
    to.state.selected = toCryptoCurrenciesState.items.find(item => item.assetId === fromRelation.to.assetId) ?? toCryptoCurrenciesState.items[0];
    relationLink.value = fromRelation.link;
    createQuote();
  },
});

const toSelected = computed({
  get: () => to.state.selected,
  set: async (option: BaseOptionModel) => {
    to.state.selected = option;
    if (option instanceof FiatOptionModel) {
      relationLink.value = option.relationLink;
    }

    if (flow.value === Flow.CRYPTO_TO_FIAT && to.state.amount) {
      createQuote();
      return;
    }

    if (flow.value !== Flow.CRYPTO_TO_CRYPTO) {
      return;
    }
    // For swap crypto we need to make sure that we have an available relation for given selection
    await fetchSwapRelations();

    const fromSelectedAssetId = from.state.selected.assetId;
    const toSelectedAssetId = to.state.selected.assetId;

    const selectedRelation = availableSwapRelations.items.find(({ from: fromService, to: toService }) => fromService.assetId === fromSelectedAssetId && toService.assetId === toSelectedAssetId);
    if (selectedRelation) {
      relationLink.value = selectedRelation.link;
      createQuote();
      return;
    }
    const toRelation = availableSwapRelations.items.find(({ to: toService }) => toService.assetId === toSelectedAssetId);
    if (!toRelation) {
      setDefaultFlowSelections(props.defaultFlowCurrencyPairs[flow.value]);
      return;
    }
    await fetchFromCryptoCurrencies();
    from.state.selected = fromCryptoCurrenciesState.items.find(item => item.assetId === toRelation.from.assetId) ?? fromCryptoCurrenciesState.items[0];
    relationLink.value = toRelation.link;
    createQuote();
  },
});

interface SelectedMethodItem {
  currency: string,
  name: string,
}

function getSelectedMethod(selectedItem: BaseOptionModel, allowedFlows: Flow[]): SelectedMethodItem {
  const defaultSelection: SelectedMethodItem = {
    currency: selectedItem.displayCurrency,
    name: selectedItem.name,
  };

  if (!allowedFlows.includes(flow.value)) {
    return defaultSelection;
  }

  const assetInfo = store.getters['assets/getAssetById'](selectedItem.currency);

  if (!assetInfo) {
    return defaultSelection;
  }

  return {
    currency: assetInfo.currency.code,
    name: assetInfo.blockchain.name,
  };
}

const fromSelectedMethod = computed<SelectedMethodItem>(() => getSelectedMethod(from.state.selected, [Flow.CRYPTO_TO_FIAT, Flow.CRYPTO_TO_CRYPTO]));
const toSelectedMethod = computed<SelectedMethodItem>(() => getSelectedMethod(to.state.selected, [Flow.FIAT_TO_CRYPTO, Flow.CRYPTO_TO_CRYPTO]));

const quote = ref<PayMethodQuoteModel | SwapCryptoQuoteModel | null>(null);
const rate = ref<ExchangeRate | null>(null);

watch([
  relationLink,
  fromCryptoFlow,
  toCryptoFlow,
  () => from.state.selected.currency,
  () => to.state.selected.currency,
], () => {
  fetchRelationDetails(relationLink.value);
  updateFormData({
    relationLink: relationLink.value,
    isServiceFromCrypto: fromCryptoFlow.value,
    isServiceToCrypto: toCryptoFlow.value,
    currencyCodeFrom: from.state.selected.currency,
    currencyCodeTo: to.state.selected.currency,
  });
});
watch(() => to.state.selected.currency, value => {
  commissions.service.currencyCode = to.state.selected.displayCurrency ?? value;
  commissions.network.currencyCode = to.state.selected.displayCurrency ?? value;
  commissions.total.currencyCode = to.state.selected.displayCurrency ?? value;
});

const { commissions, reset: resetCommissions } = useCommission(
  {
    amount: '0.00',
    currencyCode: store.getters['assets/getAssetById'](props.feeCurrency)?.currency.code ?? props.feeCurrency,
  },
  {
    amount: '0.00',
    currencyCode: store.getters['assets/getAssetById'](props.feeCurrency)?.currency.code ?? props.feeCurrency,
  },
  {
    amount: '0.00',
    currencyCode: store.getters['assets/getAssetById'](props.feeCurrency)?.currency.code ?? props.feeCurrency,
  },
);

const commissionsToShow = computed(() => {
  if (!quote.value) {
    return {
      service: commissions.service,
      network: commissions.network,
      total: commissions.total,
    };
  }

  const { feesInCrypto } = quote.value;
  const fees = feesInCrypto ? quote.value.feesInCrypto : quote.value.fees;

  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  commissions.service.currencyCode = fees.serviceFee.currencyCode;
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  commissions.network.currencyCode = fees.networkFee.currencyCode;
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  commissions.total.currencyCode = fees.totalFee.currencyCode;

  if (feesInCrypto) {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.service.amount = getFormattedCryptoAmount(fees.serviceFee.amount, commissions.service.currency);
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.network.amount = getFormattedCryptoAmount(fees.networkFee.amount, commissions.network.currency);
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.total.amount = getFormattedCryptoAmount(fees.totalFee.amount, commissions.total.currency);
  } else {
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.service.amount = fees.serviceFee.amount;
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.network.amount = fees.networkFee.amount;
    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
    commissions.total.amount = fees.totalFee.amount;
  }

  return {
    service: commissions.service,
    network: commissions.network,
    total: commissions.total,
  };
});

const getFormattedCryptoAmount = (cryptoAmount: string, cryptoCurrency: string) => {
  return formatNumberForCrypto(cryptoAmount, i18nInstanse.locale, cryptoCurrency);
};

watchEffect(() => {
  if (quote.value) {
    from.state.error = quote.value.error;
  }
});

watch(quote, () => {
  if (quote.value) {
    if (lastChanged.value !== Changed.FROM) {
      from.state.amount = quote.value.amountFrom.amount;
    }
    if (lastChanged.value !== Changed.FROM_EQUIVALENT) {
      from.state.equivalentAmount = quote.value.amountFromEquivalent?.amount ?? '';
    }
    if (lastChanged.value !== Changed.TO) {
      to.state.amount = quote.value.amountTo.amount;
    }
    if (lastChanged.value !== Changed.TO_EQUIVALENT) {
      to.state.equivalentAmount = quote.value.amountToEquivalent?.amount ?? '';
    }

    const isFeesInCrypto = quote.value.feesInCrypto && flow.value === Flow.CRYPTO_TO_CRYPTO;
    const fees = isFeesInCrypto ? quote.value.feesInCrypto : quote.value.fees;

    if (!promoCodeState.data.error && fees.feeDiscount) {
      const discountAmount = Number.parseFloat(fees.feeDiscount.amount);
      const discountCurrency = fees.feeDiscount.currencyCode;

      const amount = isFeesInCrypto
        ? `${discountCurrency} ${discountAmount.toFixed(8)}`
        : discountAmount.toLocaleString(getLocaleByLanguage(i18nInstanse.locale), {
          style: 'currency',
          currency: discountCurrency,
        });
      promoCodeState.data.capture = i18nInstanse.t('exchange-form.promo.discount', { amount }).toString();
    }
    if (promoCodeState.data.code && !promoCodeState.data.error && !fees.feeDiscount) {
      promoCodeState.data.error = i18nInstanse.t('exchange-form.promo.already-no-paybis-fee').toString();
      promoCodeState.data.capture = '';
    }
  }
});

const minMaxAmountError = computed(() => parseMinMaxAmountError(from.state.error));

const handleMinMaxAmountClick = () => {
  if (!minMaxAmountError.value) {
    return;
  }
  const isChangedFrom = quoteAmountDirection.value === Direction.FROM;
  const { amount } = minMaxAmountError.value;

  if (isChangedFrom) {
    from.state.amount = amount;
    fromAmountHandler(amount);
  } else {
    to.state.amount = amount;
    toAmountHandler(amount);
  }
};

const quoteUpdateTimeout = 60000;
const quoteTimeoutId = ref(-1);
const quoteId = ref<string | null>(null);
const quoteAmountDirection = ref<Direction | null>(null);
const { loading: createQuoteLoading, action: createQuote } = useSimpleAction(async () => {
  if (!requestedAmount.value || !parseFloat(requestedAmount.value) || parseFloat(requestedAmount.value) <= 0) {
    return;
  }

  clearTimeout(quoteTimeoutId.value);

  from.state.error = null;
  to.state.error = null;

  const params = {
    currencyCodeFrom: from.state.selected.currency,
    currencyCodeTo: to.state.selected.currency,
    requestedAmount: {
      amount: requestedAmount.value,
      currencyCode: currencyCode.value,
    },
    requestedAmountType: requestedAmountType.value,
    promoCode: promoCodeState.data.code || null,
  } as RequestQuote;

  if (flow.value === Flow.FIAT_TO_CRYPTO) {
    let response = null;

    try {
      response = await QuoteService.listBuyCryptoQuote({ ...params, paymentMethod: from.state.selected.id });
    } catch (e) {
      if (!(e instanceof QuoteValidationError)) {
        throw e;
      }
      from.state.error = e.message;
      return;
    }

    if (response.exchangeRate) {
      rate.value = {
        from: response.exchangeRate.to,
        to: response.exchangeRate.from,
        rate: formatExchangeRate(new Decimal(1).div(response.exchangeRate.rate).toString()),
      };
    } else {
      rate.value = null;
    }

    quote.value = response.paymentMethods[0];
    quoteId.value = response.id;
    quoteAmountDirection.value = params.requestedAmountType;

    const quoteItem = response.paymentMethods[0]?.item || {};
    pushToDataLayer({
      methodBuy: quoteItem.name || quoteItem.id,
      currencySell: quoteItem.amountFrom?.currencyCode,
      amountSell: quoteItem.amountFrom?.amount,
      currencyBuy: quoteItem.amountTo?.currencyCode,
      amountBuy: quoteItem.amountTo?.amount,
      promoCode: promoCodeState.data.code,
    });

    if (response.promoCode?.error) {
      promoCodeState.data.error = response.promoCode.error.message;
    } else {
      promoCodeState.data.error = '';
    }
  } else if (flow.value === Flow.CRYPTO_TO_FIAT) {
    const payload = {
      ...params,
      payoutMethod: to.state.selected.id,
    } as RequestSellCryptoQuote;
    let response;
    try {
      response = await QuoteService.listSellCryptoQuote(payload);
    } catch (e) {
      if (!(e instanceof QuoteValidationError)) {
        throw e;
      }
      from.state.error = e.message;
      return;
    }
    if (response.exchangeRate) {
      rate.value = {
        from: response.exchangeRate.from,
        to: response.exchangeRate.to,
        rate: formatExchangeRate(response.exchangeRate.rate),
      };
    } else {
      rate.value = null;
    }
    quote.value = response.payoutMethods[0];
    quoteId.value = response.id;
    quoteAmountDirection.value = params.requestedAmountType;

    if (response.promoCode?.error) {
      promoCodeState.data.error = response.promoCode.error.message;
    } else {
      promoCodeState.data.error = '';
    }
  } else if (flow.value === Flow.CRYPTO_TO_CRYPTO) {
    const payload = { ...params } as RequestSwapCryptoQuote;
    let swapQuote;
    try {
      swapQuote = await QuoteService.listSwapCryptoQuote(payload);
    } catch (e) {
      if (!(e instanceof QuoteValidationError)) {
        throw e;
      }
      from.state.error = e.message;
      return;
    }
    if (swapQuote.exchangeRate) {
      rate.value = {
        from: swapQuote.exchangeRate.from,
        to: swapQuote.exchangeRate.to,
        rate: formatExchangeRate(swapQuote.exchangeRate.rate),
      };
    } else {
      rate.value = null;
    }
    quote.value = swapQuote;
    quoteId.value = swapQuote.id;
    quoteAmountDirection.value = params.requestedAmountType;
    relationLink.value = swapQuote.relationLink;

    if (swapQuote?.item?.errors) {
      promoCodeState.data.error = swapQuote.item.errors[0].message;
    } else {
      promoCodeState.data.error = '';
    }
  }
});

watch(quote, () => {
  clearTimeout(quoteTimeoutId.value);
  if (quote.value) {
    quoteTimeoutId.value = window.setTimeout(createQuote, quoteUpdateTimeout);
  }
});

onBeforeMount(() => createQuote());

const requestQuote = debounce(createQuote, 500);

watch(() => promoCodeState.data.code, () => requestQuote());

const clearAmounts = (type: Changed) => {
  if (type !== Changed.FROM) {
    from.state.amount = '';
  }
  if (type !== Changed.FROM_EQUIVALENT) {
    from.state.equivalentAmount = '';
  }
  if (type !== Changed.TO) {
    to.state.amount = '';
  }
  if (type !== Changed.TO_EQUIVALENT) {
    to.state.equivalentAmount = '';
  }

  resetCommissions();
  if (quote.value) {
    quote.value = null;
  }
  if(rate.value) {
    rate.value = null;
  }
};

const fromLoading = computed(() => {
  if ([Changed.FROM, Changed.FROM_EQUIVALENT].includes(lastChanged.value)) {
    return false;
  }

  return createQuoteLoading.value || payoutMethodsState.loading || availableSwapRelations.loading;
});

const toLoading = computed(() => {
  if ([Changed.TO, Changed.TO_EQUIVALENT].includes(lastChanged.value)) {
    return false;
  }

  return createQuoteLoading.value || paymentMethodsState.loading || availableSwapRelations.loading;
});

const sendInputEvent = () => {
  pushToDataLayer({ event: inputEventMap[formState?.value?.lastChanged] });
};
const debouncedSendInputEvent = debounce(sendInputEvent, 5000);
const amountHandler = (value: string, type: Changed) => {
  lastChanged.value = type;

  if (from.state.amount || from.state.equivalentAmount || to.state.amount || to.state.equivalentAmount) {
    debouncedSendInputEvent();
  }

  if (value.length === 0 || Number.parseFloat(value) === 0) {
    clearAmounts(type);
    return;
  }

  requestQuote();
};

const formatExchangeRate = (rateStr: string): string => {
  let rate: Decimal;

  try {
    rate = new Decimal(rateStr);
  } catch {
    return '0';
  }

  if (rate.lte(0)) {
    return '0';
  }

  let decimals = 8;

  if (rate.gte(10)) {
    decimals = 4;
  } else if (rate.gte(0.1)) {
    decimals = 6;
  }

  const rounded = rate.toDecimalPlaces(decimals).toFixed().replace(/\.?0+$/, '');

  if (new Decimal(rounded).eq(0)) {
    const match = rateStr.match(/^0\.0*([1-9])/);
    return match ? rateStr.slice(0, rateStr.indexOf(match[1]) + 1) : '0';
  }

  return rounded;
};


const fromAmountHandler = (value: string) => amountHandler(value, Changed.FROM);
const toAmountHandler = (value: string) => amountHandler(value, Changed.TO);

document.body.addEventListener('exchange-form__from-amount', ({ detail }: { detail: string }) => {
  from.state.amount = detail;
  fromAmountHandler(detail);
});

const formState = computed(() => ({
  transactionFlow: flowMap[flow.value],
  lastChanged: lastChanged.value,
  methodSell: fromSelectedMethod.value.name,
  methodBuy: toSelectedMethod.value.name,
  currencySell: fromSelectedMethod.value.currency,
  amountSell: from.state.amount,
  currencyBuy: toSelectedMethod.value.currency,
  amountBuy: to.state.amount,
  sentFrom: ExchangeFormSource.MAIN_PAGE_KANO,
  promoCode: promoCodeState.data.code,
}));
watch(formState, (value) => {
  pushToDataLayer(value);
}, { immediate: true });
</script>
